import Head from 'next/head'

export type SEOMetadataProps = {
  additionalLinkTags?: Array<{
    type?: string
    rel?: string
    href?: string
    sizes?: string
    title?: string
  }>
  additionalMetaTags?: Array<{
    name?: string
    content?: string
  }>
  additionalScriptTags?: Array<{
    type?: string
    src?: string
    name?: string
  }>
  description?: string
  imageUrl?: string
  link?: any[]
  meta?: any[]
  facebookAppId?: string
  noFollow?: boolean
  noIndex?: boolean
  script?: any[]
  title?: string
  twitterHandle?: string
  type?: string | 'website' | 'article' | 'profile'
  url?: string
  videoUrl?: string
}

export const SEOMetadata = (props: SEOMetadataProps) => {
  const meta: SEOMetadataProps['additionalMetaTags'] = []
  const links: SEOMetadataProps['additionalLinkTags'] = []
  const scripts: SEOMetadataProps['additionalScriptTags'] = []

  const formattedTitle = props.title ? `${props.title} | Startups.com` : null

  if (props.additionalLinkTags) {
    links.push(...props.additionalLinkTags)
  }

  if (props.additionalMetaTags) {
    meta.push(...props.additionalMetaTags)
  }

  if (props.description) {
    meta.push(
      { name: 'description', content: props.description },
      { name: 'og:description', content: props.description },
      { name: 'twitter:description', content: props.description }
    )
  }

  if (props.title) {
    meta.push({ name: 'twitter:title', content: props.title }, { name: 'og:title', content: props.title })
  }

  if (props.facebookAppId) {
    meta.push({ name: 'fb:app_id', content: props.facebookAppId })
  }

  if (props.twitterHandle) {
    meta.push({ name: 'twitter:site', content: props.twitterHandle })
  }

  if (props.imageUrl && props.title) {
    meta.push(
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: props.imageUrl },
      { name: 'twitter:image:alt', content: props.title }
    )
  }

  if (props.imageUrl) {
    meta.push({ name: 'og:image', content: props.imageUrl })
  }

  if (props.type) {
    meta.push({ name: 'og:type', content: props.type })
  }

  if (props.url) {
    links.push({ rel: 'canonical', href: props.url })
    meta.push({ name: 'og:url', content: props.url })
  }

  if (props.videoUrl) {
    meta.push({ name: 'og:video', content: props.videoUrl })
  }

  if (props.noIndex && props.noFollow) {
    meta.push({ name: 'robots', content: 'noindex,nofollow' })
  } else if (props.noIndex) {
    meta.push({ name: 'robots', content: 'noindex' })
  } else if (props.noFollow) {
    meta.push({ name: 'robots', content: 'nofollow' })
  }

  meta.push(
    { name: 'fb:app_id', content: '1858686837721825' },
    { name: 'msapplication-TileColor', content: '#1A1B20' },
    { name: 'msapplication-TileImage', content: '/assets/meta/mstile-144x144.png' },
    { name: 'msapplication-config', content: '/assets/meta/browserconfig.xml' }
  )

  if (props.additionalScriptTags) {
    scripts.push(...props.additionalScriptTags)
  }

  return (
    <Head>
      {formattedTitle && <title key={'site-title'}>{formattedTitle}</title>}

      {meta.map((m, i) => {
        const metaProps: { property?: string; name?: string; content?: string } = {}

        if (m.name?.startsWith('og:') || m.name?.startsWith('fb:') || m.name?.startsWith('twitter:')) {
          metaProps.property = m.name
        } else {
          metaProps.name = m.name
        }

        metaProps.content = m.content

        return <meta key={m.name || i} {...metaProps} />
      })}

      {links.map((l, i) => (
        <link key={`${l.rel || i}.${l.sizes}`} {...l} />
      ))}

      {scripts.map((s, i) => (
        <script key={s.name || s.src || i} {...s} />
      ))}
    </Head>
  )
}
